import React, { useState, useMemo, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import {
  FiArrowUpRight,
  FiClock,
  FiChevronLeft,
  FiChevronRight,
  FiMoreVertical,
  FiEye,
  FiCopy,
  FiPlus,
  FiKey,
  FiLoader,
  FiX,
  FiEyeOff,
  FiXCircle,
  FiCheckCircle,
  FiTrash2,
} from 'react-icons/fi';
// Make sure to install recharts: yarn add recharts
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
  ResponsiveContainer,
  Line,
} from 'recharts';

// Import your react-query hooks
import {
  usePaginatedAasApiKeys,
  useDeleteAasApiKey,
  useEnableAasApiKey,
  useDisableAasApiKey,
  useCreateAasApiKey,
  useDashboardStats,
  usePaginatedDashboardStats,
} from '../../../helpers/api/aas_api_keys/aas_api_keys';
import { usePaginatedRequestHistoryForDashboard } from '../../../helpers/api/request_history/requets_history';
import { PiFileImage } from 'react-icons/pi';
import ConfirmModal from './ConfirmModal';
import { message } from 'antd';
import { tToast } from '../../../helpers/i18n/toastI18n';
import DocsPage from '../DocsPage/docs.page';
import { useHtmlDataTheme } from '../../../services/useHtmlDataTheme';
import { StatCard } from '../../ComponnentsUI/common/StatCard';
import { NoDataPlaceholder } from '../../ComponnentsUI/common/NoDataPlaceholder';
import useDocumentTitle from '../../../hooks/useDocumentTitle';

// --- TYPE DEFINITIONS ---

// **FIXED**: id is now a string to match your API's UUID
interface AasApiKey {
  id: string;
  name: string;
  value: string;
  created_at: string;
  expire_at: string;
  last_used_at: string | null;
  is_disabled: boolean;
}

interface RequestHistoryItem {
  id: string; // Assuming request history also uses UUIDs
  endpoint: string;
  method: 'GET' | 'POST' | 'PUT' | 'DELETE';
  status_code: number;
  response_time_ms: number;
  created_at: string;
  number_of_images?: number;
}

// **FIXED**: This interface now perfectly matches your API's pagination structure
interface PaginatedResponse<T> {
  data: {
    data: T[]; // Changed from `items` to `data`
    total_count: number; // Changed from `total` to `total_count`
    page: number;
    items_per_page: number; // Changed from `size`
  };
}

// --- MODAL COMPONENT ---
// This component remains the same as it's not affected by the response structure.
interface CreateAasApiKeyModalProps {
  isOpen: boolean;
  onClose: () => void;
  onCreate: (name: string, expireAt: string) => void;
  isCreating: boolean;
}

const CreateAasApiKeyModal: React.FC<CreateAasApiKeyModalProps> = ({
  isOpen,
  onClose,
  onCreate,
  isCreating,
}) => {
  const { t } = useTranslation('ApiAsService');
  const [name, setName] = useState('');
  const [expireAt, setExpireAt] = useState('');

  const defaultExpiration = useMemo(() => {
    const date = new Date();
    date.setFullYear(date.getFullYear() + 1);
    return date.toISOString().slice(0, 16);
  }, []);

  if (!isOpen) return null;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const finalExpireAt = expireAt || defaultExpiration;
    if (name && finalExpireAt) {
      onCreate(name, finalExpireAt);
    }
  };

  document.title = 'DEEPECHO - API As Service';

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50"
      onClick={onClose}
    >
      <div
        className="bg-card-color-100 dark:bg-gray-800 p-6 sm:p-8 rounded-2xl shadow-xl w-full max-w-md mx-4"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-xl font-bold text-title-page-color-100 dark:text-white">
            {t('keys.modal.title')}
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 dark:text-gray-300 dark:hover:text-gray-100"
          >
            <FiX size={24} />
          </button>
        </div>
        <form onSubmit={handleSubmit}>
          <div className="mb-4">
            <label
              htmlFor="keyName"
              className="block text-sm font-medium text-title-page-color-100 dark:text-white mb-2"
            >
              {t('keys.modal.nameLabel')}
            </label>
            <input
              type="text"
              id="keyName"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder={t('keys.modal.namePlaceholder')}
              className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-blue-500 focus:border-blue-500 text-base bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              required
            />
          </div>
          <div className="mb-8">
            <label
              htmlFor="expireAt"
              className="block text-sm font-medium text-title-page-color-100 dark:text-white mb-2"
            >
              {t('keys.modal.expirationLabel')}
            </label>
            <input
              type="datetime-local"
              id="expireAt"
              defaultValue={defaultExpiration}
              onChange={(e) => setExpireAt(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-blue-500 focus:border-blue-500 text-base bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              required
            />
          </div>
          <div className="flex justify-end gap-4">
            <button
              type="button"
              onClick={onClose}
              className="px-6 py-2 rounded-lg bg-card-color-200 dark:bg-gray-700 text-title-page-color-100 dark:text-white font-semibold text-sm hover:bg-gray-300 dark:hover:bg-gray-600"
            >
              {t('keys.modal.cancel')}
            </button>
            <button
              type="submit"
              disabled={isCreating}
              className="px-6 py-2 rounded-lg bg-blue-600 text-white font-semibold hover:bg-blue-700 disabled:bg-blue-300 dark:disabled:bg-blue-800 flex items-center gap-2 text-sm"
            >
              {isCreating && <FiLoader className="animate-spin" />}{' '}
              {isCreating ? t('keys.modal.creating') : t('keys.modal.createKey')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// --- HELPER COMPONENTS ---

// **FIXED**: onAction now accepts a string for keyId
interface AasApiKeyCardProps {
  apiKey: AasApiKey;
  onAction: (action: 'enable' | 'disable' | 'delete', keyId: string) => void;
}

const AasApiKeyCard: React.FC<AasApiKeyCardProps> = ({ apiKey, onAction }) => {
  const { t, i18n: i18nInstance } = useTranslation('ApiAsService');
  const [copied, setCopied] = useState<boolean>(false);
  const [isKeyVisible, setIsKeyVisible] = useState<boolean>(false);
  
  // State and ref for the dropdown menu
  const [isMenuOpen, setIsMenuOpen] = useState<boolean>(false);
  const menuRef = useRef<HTMLDivElement>(null);

  const handleCopy = () => {
    navigator.clipboard.writeText(apiKey.value);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  // Effect to close the menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setIsMenuOpen(false);
      }
    };

    if (isMenuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isMenuOpen]);


  const isExpired = new Date(apiKey.expire_at) < new Date();
  const status = isExpired
    ? t('keys.card.status.expired')
    : apiKey.is_disabled
      ? t('keys.card.status.inactive')
      : t('keys.card.status.active');
  const statusColor = isExpired 
    ? 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400' 
    : apiKey.is_disabled 
      ? 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-400' 
      : 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400';

  return (
    <div className="bg-card-color-100 dark:bg-gray-800 p-6 rounded-2xl border border-gray-200 dark:border-gray-700 shadow-sm">
      {/* Header Section */}
      <div className="flex justify-between items-start mb-5">
        <div className="flex items-center gap-4">
          <div className="bg-blue-50 dark:bg-blue-900/30 p-3 rounded-full">
            <FiKey className="h-5 w-5 text-blue-500 dark:text-blue-400" />
          </div>
          <div>
            <h3 className="font-semibold text-title-page-color-100 dark:text-white text-base">{apiKey.name}</h3>
            <p className="text-xs text-title-page-color-300 dark:text-gray-400">
              {t('keys.card.createdOn', {
                date: new Date(apiKey.created_at).toLocaleDateString(
                  i18nInstance.language?.startsWith('fr') ? 'fr-FR' : 'en-US'
                ),
              })}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-4">
          <span className={`px-3 py-1 text-xs font-semibold rounded-full ${statusColor}`}>
            {status}
          </span>
          {/* Dropdown Menu Trigger */}
          <div className="relative" ref={menuRef}>
            <button onClick={() => setIsMenuOpen(!isMenuOpen)} className="text-title-page-color-300 dark:text-gray-400 hover:text-gray-600 dark:hover:text-gray-200">
              <FiMoreVertical size={20} />
            </button>
            {/* Dropdown Menu Content */}
            {isMenuOpen && (
              <div className="absolute top-full right-0 mt-2 w-48 bg-card-color-100 dark:bg-gray-800 rounded-lg shadow-xl border border-gray-100 dark:border-gray-700 z-10 py-1">
                {/* Enable/Disable Button */}
                {!isExpired && (
                  apiKey.is_disabled ? (
                    <button onClick={() => { onAction('enable', apiKey.id); setIsMenuOpen(false); }} className="w-full text-left px-4 py-2 text-sm text-title-page-color-100 dark:text-white hover:bg-card-color-200 dark:hover:bg-gray-700 flex items-center gap-3">
                      <FiCheckCircle className="text-green-500 dark:text-green-400" /> {t('keys.card.enable')}
                    </button>
                  ) : (
                    <button onClick={() => { onAction('disable', apiKey.id); setIsMenuOpen(false); }} className="w-full text-left px-4 py-2 text-sm text-title-page-color-100 dark:text-white hover:bg-card-color-200 dark:hover:bg-gray-700 flex items-center gap-3">
                      <FiXCircle className="text-yellow-500 dark:text-yellow-400" /> {t('keys.card.disable')}
                    </button>
                  )
                )}
                {/* Delete Button */}
                <button onClick={() => { onAction('delete', apiKey.id); setIsMenuOpen(false); }} className="w-full text-left px-4 py-2 text-sm text-title-page-color-100 dark:text-white hover:bg-card-color-200 dark:hover:bg-gray-700 flex items-center gap-3">
                  <FiTrash2 className="text-red-500 dark:text-red-400" /> {t('keys.card.delete')}
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* API Key Display Section */}
      <div className="mb-5">
        <p className="text-xs font-medium text-gray-400 dark:text-gray-500 uppercase tracking-wider mb-2">{t('keys.card.apiKeyLabel')}</p>
        <div className="flex items-center justify-between bg-gray-50 dark:bg-gray-700 p-3 rounded-lg border border-gray-200 dark:border-gray-600">
          <span className="font-mono text-gray-700 dark:text-gray-300 text-sm break-all">
            {isKeyVisible ? apiKey.value : `••••••••••••••••••••${apiKey.value.slice(-4)}`}
          </span>
          <div className="flex items-center gap-4 pl-4">
            <button onClick={() => setIsKeyVisible(!isKeyVisible)} className="text-gray-500 dark:text-gray-400 hover:text-blue-600 dark:hover:text-blue-400">
              {isKeyVisible ? <FiEyeOff size={16} /> : <FiEye size={16} />}
            </button>
            <button onClick={handleCopy} className="text-gray-500 dark:text-gray-400 hover:text-blue-600 dark:hover:text-blue-400">
              {copied ? <span className="text-green-600 dark:text-green-400 text-xs font-semibold">{t('keys.card.copied')}</span> : <FiCopy size={16} />}
            </button>
          </div>
        </div>
      </div>
      
      {/* Footer Section (Replaces the old button area) */}
      <div className="flex justify-between items-center text-xs text-gray-500 dark:text-gray-400">
          <span>{t('keys.card.expires')} <span className="font-medium text-gray-700 dark:text-gray-300">{new Date(apiKey.expire_at).toLocaleString(i18nInstance.language?.startsWith('fr') ? 'fr-FR' : 'en-US')}</span></span>
          <span>{t('keys.card.lastUsed')} <span className="font-medium text-gray-700 dark:text-gray-300">{apiKey.last_used_at ? new Date(apiKey.last_used_at).toLocaleString(i18nInstance.language?.startsWith('fr') ? 'fr-FR' : 'en-US') : t('keys.card.never')}</span></span>
      </div>
    </div>
  );
};
interface EvolutionChartProps {
  title: string;
  description: string;
  data: RequestHistoryItem[] | undefined;
  dataKey: 'number_of_images' | 'requests';
  timeRange: number;
  setTimeRange: React.Dispatch<React.SetStateAction<number>>;
}
const EvolutionChart: React.FC<EvolutionChartProps> = ({
  title,
  description,
  data,
  dataKey,
  timeRange,
  setTimeRange,
}) => {
  const { t } = useTranslation('ApiAsService');
  const processedData = useMemo(() => {
    if (!data || data.length === 0) return [];
    const now = new Date();
    const monthNames = [
      'Jan',
      'Feb',
      'Mar',
      'Apr',
      'May',
      'Jun',
      'Jul',
      'Aug',
      'Sep',
      'Oct',
      'Nov',
      'Dec',
    ];
    let result = [];
    for (let i = timeRange - 1; i >= 0; i--) {
      const date = new Date(now.getFullYear(), now.getMonth() - i, 1);
      const prevYearDate = new Date(
        now.getFullYear() - 1,
        now.getMonth() - i,
        1
      );
      const monthStr = monthNames[date.getMonth()];
      const filterByMonth = (arr: RequestHistoryItem[], targetDate: Date) =>
        arr.filter((d) => {
          const dDate = new Date(d.created_at);
          return (
            dDate.getFullYear() === targetDate.getFullYear() &&
            dDate.getMonth() === targetDate.getMonth()
          );
        });
      const getValue = (arr: RequestHistoryItem[]) =>
        arr.reduce(
          (sum, item) =>
            sum +
            (dataKey === 'number_of_images' ? item.number_of_images || 0 : 1),
          0
        );
      result.push({
        month: monthStr,
        current: getValue(filterByMonth(data, date)),
        previous: getValue(filterByMonth(data, prevYearDate)),
      });
    }
    return result;
  }, [data, dataKey, timeRange]);
  const trend = useMemo(() => {
    if (processedData.length < 2) return 0;
    const last = processedData[processedData.length - 1].current;
    const secondLast = processedData[processedData.length - 2].current;
    if (secondLast === 0) return last > 0 ? 100 : 0;
    return parseFloat((((last - secondLast) / secondLast) * 100).toFixed(1));
  }, [processedData]);
  const peak = useMemo(
    () => Math.max(0, ...processedData.map((d) => d.current)),
    [processedData]
  );
  const hasData = processedData.some((d) => d.current > 0 || d.previous > 0);
  return (
    <div className="bg-card-color-100 dark:bg-gray-800 p-6 rounded-2xl shadow-sm border border-gray-200 dark:border-gray-700 h-full flex flex-col">
      <div className="flex justify-between items-center mb-4">
        <div>
          <h3 className="text-base font-bold text-title-page-color-100 dark:text-white">{title}</h3>
          <p className="text-xs text-gray-500 dark:text-gray-400">{description}</p>
        </div>
        <div className="flex items-center gap-1 bg-gray-100 dark:bg-gray-700 p-1 rounded-lg">
          {[12, 6, 3].map((range) => (
            <button
              key={range}
              onClick={() => setTimeRange(range)}
              className={`px-3 py-1 text-xs font-semibold rounded-md ${timeRange === range ? 'bg-white dark:bg-gray-600 shadow-sm text-blue-600 dark:text-blue-400' : 'text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'}`}
            >
              {range}M
            </button>
          ))}
        </div>
      </div>
      <div className="flex-grow min-h-[250px]">
        {hasData ? (
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart
              data={processedData}
              margin={{ top: 5, right: 20, left: -10, bottom: 5 }}
            >
              <defs>
                <linearGradient
                  id="colorCurrentBlue"
                  x1="0"
                  y1="0"
                  x2="0"
                  y2="1"
                >
                  <stop offset="5%" stopColor="#60a5fa" stopOpacity={0.4} />
                  <stop offset="95%" stopColor="#60a5fa" stopOpacity={0} />
                </linearGradient>
              </defs>
              <XAxis
                dataKey="month"
                tick={{ fontSize: 10 }}
                stroke="#9ca3af"
                axisLine={false}
                tickLine={false}
              />
              <YAxis
                tick={{ fontSize: 10 }}
                stroke="#9ca3af"
                axisLine={false}
                tickLine={false}
              />
              <Tooltip
                contentStyle={{
                  backgroundColor: 'white',
                  border: '1px solid #e5e7eb',
                  borderRadius: '0.5rem',
                  fontSize: '12px',
                }}
              />
              <Legend
                verticalAlign="top"
                align="right"
                iconType="circle"
                wrapperStyle={{ paddingBottom: '20px', fontSize: '12px' }}
                formatter={(value) => (
                  <span className="text-gray-600">{String(value)}</span>
                )}
                payload={[
                  { value: t('charts.legend.currentPeriod'), type: 'line', color: '#3b82f6' },
                  { value: t('charts.legend.previousPeriod'), type: 'line', color: '#93c5fd' },
                ]}
              />
              <Area
                type="monotone"
                dataKey="current"
                stroke="#3b82f6"
                strokeWidth={2}
                fillOpacity={1}
                fill="url(#colorCurrentBlue)"
                name="Current Period"
              />
              <Line
                type="monotone"
                dataKey="previous"
                stroke="#93c5fd"
                strokeWidth={2}
                strokeDasharray="5 5"
                name="Previous Period"
              />
            </AreaChart>
          </ResponsiveContainer>
        ) : (
          <NoDataPlaceholder className="h-full" />
        )}
      </div>
      {hasData && (
        <div className="mt-4 flex gap-6 text-xs border-t border-gray-100 dark:border-gray-700 pt-4">
          <div>
            <span className="text-gray-500 dark:text-gray-400">{t('dashboard.charts.trend')}</span>
            <p
              className={`font-bold ${trend >= 0 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}
            >
              {trend >= 0 ? '+' : ''}
              {trend}%
            </p>
          </div>
          <div>
            <span className="text-gray-500 dark:text-gray-400">{t('dashboard.charts.peak')}</span>
            <p className="font-bold text-title-page-color-100 dark:text-white">{peak.toLocaleString()}</p>
          </div>
        </div>
      )}
    </div>
  );
};

// --- MAIN PAGE COMPONENTS ---
interface DashboardStats {
  stats: {
    total_requests: number;
    active_keys: number;
    success_rate: number;
    total_images: number;
    avg_response_ms: number;
  };
  recent_requests: any[]; // Use the RequestHistoryItem type here
  chart_data: any[]; // Use the RequestHistoryItem type here
}
const APIDashboard: React.FC = () => {
  const { t } = useTranslation('ApiAsService');
  useDocumentTitle('DEEPECHO - API Dashboard');
   const [showAllRequests, setShowAllRequests] = useState(false);
    const [historyCurrentPage, setHistoryCurrentPage] = useState(1);
    const historyPageSize = 6;
  const [imageChartRange, setImageChartRange] = useState<number>(12);
  const [apiChartRange, setApiChartRange] = useState<number>(12);
     const { 
        data: dashboardData, 
        isLoading: dashboardLoading 
    } = usePaginatedDashboardStats(
        !showAllRequests ? historyCurrentPage:undefined,
        !showAllRequests ? historyPageSize:undefined
    ) as {
        data: { data: DashboardStats };
        isLoading: boolean;
    };
    const stats = dashboardData?.stats;
    const recentRequestsV = dashboardData?.all_requests_paginated?.data|| dashboardData?.recent_requests || [];
    const chartData = dashboardData?.chart_data || [];
    const allHistoryItems =  dashboardData?.recent_requests|| [];
    const itemsToDisplay = showAllRequests ? allHistoryItems : recentRequestsV;

    console.log('Dashboard Data:', dashboardData);
  // **FIXED**: Accessing .data.data and .data.total_count
 
  const statCardsData = [
    {
      title: t('dashboard.stats.totalRequests'),
      value:  (stats?.total_requests || 0).toLocaleString(),
      icon: <FiArrowUpRight size={20} />,
      color: '#2563eb',
    },
    {
      title: t('dashboard.stats.activeKeys'),
      value: String(stats?.active_keys || 0),
      icon: <FiKey size={20} />,
      color: '#0ea5e9',
    },
    {
      title: t('dashboard.stats.successRate'),
      value: `${stats?.success_rate || 0}%`,
      icon: <FiArrowUpRight size={20} />,
      color: '#10b981',
    },
    {
      title: t('dashboard.stats.totalImages'),
      value: `${stats?.total_images || 0}`,
      icon: <PiFileImage size={20} />,
      color: '#6b7280',
    },
  ];
  const getStatusColor = (status: any) =>
    status == "success"
      ? 'bg-green-500'
      : status == "fail"
        ? 'bg-red-500'
        : 'bg-yellow-500';
  return (
    <div className="space-y-8">
      <div>
        <p className="text-gray-500 dark:text-gray-400 mt-1 text-sm">
          {t('dashboard.subtitle')}
        </p>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
        {statCardsData.map((card) => (
          <StatCard key={card.title} {...card} />
        ))}
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        <EvolutionChart
          title={t('dashboard.charts.imageRequests.title')}
          description={t('dashboard.charts.imageRequests.description')}
          data={chartData}
          dataKey="number_of_images"
          timeRange={imageChartRange}
          setTimeRange={setImageChartRange}
        />
        <EvolutionChart
          title={t('dashboard.charts.apiUsage.title')}
          description={t('dashboard.charts.apiUsage.description')}
          data={chartData}
          dataKey="requests"
          timeRange={apiChartRange}
          setTimeRange={setApiChartRange}
        />
      </div>
      <div className="bg-card-color-100 dark:bg-gray-800 p-6 rounded-2xl shadow-sm border border-gray-200 dark:border-gray-700">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-base font-bold text-title-page-color-100 dark:text-white">{showAllRequests ? t('dashboard.requests.allTitle') : t('dashboard.requests.recentTitle')}</h3>
           <p className="text-sm text-gray-500 dark:text-gray-400">
                        {showAllRequests 
                            ? t('dashboard.requests.page', { page: historyCurrentPage })
                            : t('dashboard.requests.showingLatest', { count: itemsToDisplay.length })
                        }
                    </p>
        </div>
        {dashboardLoading ? (
          <div className="flex justify-center items-center h-40">
            <FiLoader className="animate-spin text-blue-500 dark:text-blue-400" size={32} />
          </div>
        ) : recentRequestsV.length > 0 ? (
          <>
            <div className="hidden md:grid grid-cols-12 gap-4 text-xs font-semibold text-gray-500 dark:text-gray-400 px-4 py-2">
              <div className="col-span-5">{t('dashboard.requests.columns.endpoint')}</div>
              <div className="col-span-2">{t('dashboard.requests.columns.images')}</div>
              <div className="col-span-1">{t('dashboard.requests.columns.status')}</div>
              {/* <div className="col-span-2">Response Time</div> */}
              <div className="col-span-2 text-right">{t('dashboard.requests.columns.createdAt')}</div>
            </div>
            {recentRequestsV.map((req) => (
              <div
                key={req.id}
                className="grid grid-cols-12 gap-4 items-center bg-card-color-200 dark:bg-gray-700 rounded-lg px-4 py-3 text-xs mb-1"
              >
                <div className="col-span-12 md:col-span-5 flex items-center gap-3">
                  <span
                    className={`w-2 h-2 rounded-full ${getStatusColor(req.status)}`}
                  ></span>
                  <span className="font-mono text-title-page-color-100 dark:text-white truncate">
                    {req.endpoint}
                  </span>
                </div>
                {/* <div className="col-span-6 md:col-span-2">
                  <span
                    className={`px-2 py-0.5 text-xs font-bold rounded ${req.method === 'POST' ? 'bg-blue-100 text-blue-800' : 'bg-green-100 text-green-800'}`}
                  >
                    {req.method}
                  </span>
                </div> */}
                <div className="col-span-6 md:col-span-2 text-title-page-color-100 dark:text-white">
                  {req.number_of_images || 0}
                </div>
                <div className="col-span-6 md:col-span-1 font-semibold text-title-page-color-100 dark:text-white">
                  {req.status}
                </div>
                {/* <div className="col-span-6 md:col-span-2 text-gray-600">
                  {req.response_time_ms}ms
                </div> */}
                <div className="col-span-6 md:col-span-2 text-right text-title-page-color-100 dark:text-white">
                  {new Date(req.created_at).toLocaleTimeString()}
                </div>
              </div>
            ))}
            <div className="text-right mt-6">
                    <button onClick={() => { setShowAllRequests(!showAllRequests); setHistoryCurrentPage(1); }} className="text-blue-600 dark:text-blue-400 font-semibold hover:underline text-sm">
                        {showAllRequests ? t('dashboard.requests.viewRecent') : t('dashboard.requests.viewAll')}
                    </button>
                </div>
          </>
        ) : (
          <div className="text-center py-10 text-gray-500 dark:text-gray-400">
            {t('dashboard.requests.empty')}
          </div>
        )}
      </div>
    </div>
  );
};
interface AasApiKeyContentProps {
  topOfListRef: React.RefObject<HTMLDivElement>;
}
const AasApiKeyContent: React.FC<AasApiKeyContentProps> = ({topOfListRef}) => {
  const { t } = useTranslation('ApiAsService');
  useDocumentTitle('DEEPECHO - API Keys');
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const pageSize = 3;
  const {
    data: apiKeysData,
    isLoading,
    refetch,
  } = usePaginatedAasApiKeys(currentPage,pageSize ) as {
    data: PaginatedResponse<AasApiKey>;
    isLoading: boolean;
    refetch: () => void;
  };
  const { mutate: deleteAasApiKey } = useDeleteAasApiKey();
  const { mutate: enableAasApiKey } = useEnableAasApiKey();
  const { mutate: disableAasApiKey } = useDisableAasApiKey();
  const { mutate: createAasApiKey, isLoading: isCreating } = useCreateAasApiKey();
 const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);
  const [pendingAction, setPendingAction] = useState<{
    action: 'enable' | 'disable' | 'delete';
    keyId: string;
  } | null>(null);
  const handleCreateKey = (name: string, expireAt: string) => {
    createAasApiKey(
      { name, expire_at: expireAt },
      {
        onSuccess: () => {
          setIsModalOpen(false);
          refetch();
        },
        onError: (error) => {
          // console.error('Failed to create API key:', error);
          message.error(
            error?.details?.error || tToast('api.keyCreateFailed')
          );
         
        },
      }
    );
  };
   const handleKeyAction = (
    action: 'enable' | 'disable' | 'delete',
    keyId: string
  ) => {
    setPendingAction({ action, keyId });
    setIsConfirmModalOpen(true);
  };
  const executePendingAction = () => {
    if (!pendingAction) return;

    const { action, keyId } = pendingAction;
    const mutationMap = {
      enable: enableAasApiKey,
      disable: disableAasApiKey,
      delete: deleteAasApiKey,
    };
    
    mutationMap[action](keyId, { onSuccess: refetch });

    // Close the modal and clear the pending action
    setIsConfirmModalOpen(false);
    setPendingAction(null);
  };
  console.log('API Keys Data:', apiKeysData); // Debugging line to check the structure of apiKeysData
  // **FIXED**: Accessing .data.total_count and .data.data
  const totalKeys = apiKeysData?.total_count || 0;
  const totalPages = Math.ceil(totalKeys / pageSize);
  const keyItems = apiKeysData?.items || [];
  console.log('Key Items:', apiKeysData); // Debugging line to check the structure of keyItems
  useEffect(() => {
    refetch()
    topOfListRef?.current?.scrollIntoView({ behavior: 'smooth' });
  }, [currentPage,refetch]);
  return (
    <div className="space-y-8">
       <ConfirmModal
        isOpen={isConfirmModalOpen}
        onClose={() => {
          setIsConfirmModalOpen(false);
          setPendingAction(null);
        }}
        onConfirm={executePendingAction}
        title={t('keys.confirm.title', {
          action: pendingAction?.action
            ? t(`keys.actions.${pendingAction.action}`)
            : '',
        })}
        content={
          pendingAction?.action === 'delete'
            ? t('keys.confirm.deleteContent')
            : t('keys.confirm.reversibleContent')
        }
        confirmText={t('keys.confirm.yesAction', {
          action: pendingAction?.action
            ? t(`keys.actions.${pendingAction.action}`)
            : '',
        })}
        cancelText={t('keys.confirm.cancel')}
        isDestructive={pendingAction?.action === 'delete'}
      />
      <CreateAasApiKeyModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onCreate={handleCreateKey}
        isCreating={isCreating}
      />
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <p className="text-gray-500 dark:text-gray-400 mt-1 text-sm">
            {t('keys.subtitle')}
          </p>
        </div>
        <button
          onClick={() => setIsModalOpen(true)}
          className="flex items-center gap-2 px-4 py-2 font-semibold text-white bg-blue-600 rounded-lg shadow-md hover:bg-blue-700 text-sm"
        >
          <FiPlus size={16} /> {t('keys.create')}
        </button>
      </div>
      {isLoading ? (
        <div className="flex justify-center items-center h-64">
          <FiLoader className="animate-spin text-blue-500 dark:text-blue-400" size={40} />
        </div>
      ) : keyItems.length > 0 ? (
        <div className="space-y-6">
          {keyItems.map((key) => (
            <AasApiKeyCard key={key.id} apiKey={key} onAction={handleKeyAction} />
          ))}
        </div>
      ) : (
        <div className="text-center py-20 bg-card-color-100 dark:bg-gray-800 rounded-2xl border border-gray-200 dark:border-gray-700">
          <FiKey className="mx-auto text-gray-300 dark:text-gray-600" size={48} />
          <h3 className="mt-2 text-lg font-semibold text-title-page-color-100 dark:text-white">
            {t('keys.empty.title')}
          </h3>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            {t('keys.empty.description')}
          </p>
          <button
            onClick={() => setIsModalOpen(true)}
            className="mt-4 flex mx-auto items-center gap-2 px-4 py-2 font-semibold text-white bg-blue-600 rounded-lg shadow-md hover:bg-blue-700 text-sm"
          >
            <FiPlus size={16} /> {t('keys.create')}
          </button>
        </div>
      )}
      {totalKeys > pageSize && (
        <div className="flex flex-col sm:flex-row justify-between items-center gap-4 bg-card-color-100 dark:bg-gray-800 p-4 rounded-2xl shadow-sm border border-gray-200 dark:border-gray-700">
          <p className="text-xs text-title-page-color-300 dark:text-gray-400">
            {t('keys.pagination.showing', {
              from: (currentPage - 1) * pageSize + 1,
              to: Math.min(currentPage * pageSize, totalKeys),
              total: totalKeys,
            })}
          </p>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setCurrentPage((p) => p - 1)}
              disabled={currentPage === 1}
              className="p-2 rounded-lg text-title-page-color-100 dark:text-white border border-gray-300 dark:border-gray-600 bg-card-color-100 dark:bg-gray-700 hover:bg-gray-100 dark:hover:bg-gray-600 hover:text-gray-800 dark:hover:text-gray-200 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <FiChevronLeft />
            </button>
            {[...Array(totalPages).keys()].map((p) => (
              <button
                key={p + 1}
                onClick={() => setCurrentPage(p + 1)}
                className={`px-3 py-1 text-xs rounded-lg ${currentPage === p + 1 ? 'bg-blue-600 text-white' : 'hover:bg-gray-100 dark:hover:bg-gray-600 hover:text-gray-800 dark:hover:text-gray-200 bg-card-color-100 dark:bg-gray-700 text-title-page-color-100 dark:text-white'}`}
              >
                {p + 1}
              </button>
            ))}
            <button
              onClick={() => setCurrentPage((p) => p + 1)}
              disabled={currentPage === totalPages}
              className="p-2 rounded-lg border border-gray-300 dark:border-gray-600 text-title-page-color-100 dark:text-white bg-card-color-100 dark:bg-gray-700 hover:bg-gray-100 dark:hover:bg-gray-600 hover:text-gray-800 dark:hover:text-gray-200 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <FiChevronRight />
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

// --- ROOT COMPONENT ---
const ApiAsService: React.FC = () => {
  const { t } = useTranslation('ApiAsService');
  const [activeView, setActiveView] = useState<'dashboard' | 'keys'>(
    'dashboard'
  );
  const topOfListRef = useRef<HTMLDivElement>(null);
  const theme = useHtmlDataTheme();
  const navButtonStyle =
    'px-4 py-1.5 rounded-lg font-semibold transition-colors duration-200 text-sm';
  const activeStyle = 'bg-blue-600 text-white shadow-md';
  const inactiveStyle = 'text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700';
  return (
    <div className="bg-card-color-200 dark:bg-gray-900 min-h-screen" ref={topOfListRef}>
      {/* Sticky Navigation */}
      <div className="sticky top-0 z-0 bg-card-color-200 dark:bg-gray-900 pt-4 sm:pt-8 pb-4">
        <div className="max-w-7xl mx-auto px-4 sm:px-8">
          <div className="bg-card-color-100 dark:bg-gray-800 p-1.5 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 inline-flex items-center gap-2">
            <button
              onClick={() => setActiveView('dashboard')}
              className={`${navButtonStyle} ${activeView === 'dashboard' ? activeStyle : inactiveStyle}`}
            >
              {t('tabs.dashboard')}
            </button>
            <button
              onClick={() => setActiveView('keys')}
              className={`${navButtonStyle} ${activeView === 'keys' ? activeStyle : inactiveStyle}`}
            >
              {t('tabs.keys')}
            </button>
            <button
              onClick={() => setActiveView('docs')}
              className={`${navButtonStyle} ${activeView === 'docs' ? activeStyle : inactiveStyle}`}
            >
              {t('tabs.docs')}
            </button>
          </div>
        </div>
      </div>
      
      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-8 pb-4 sm:pb-8">
        {activeView === 'dashboard' ? <APIDashboard /> : activeView === 'keys' ? <AasApiKeyContent topOfListRef={topOfListRef}/> : <DocsPage />}
      </div>
    </div>
  );
};

export default ApiAsService;